import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

export interface Crumb {
  label: ReactNode;
  to?: string;
}

/**
 * Story 46.7 — universal breadcrumbs.
 *
 * Detail pages render this above their main content. Each crumb is
 * either a Link (when `to` is set) or a plain span. Truncates
 * gracefully on narrow viewports.
 */
export function Breadcrumbs({ crumbs }: { crumbs: Crumb[] }) {
  return (
    <nav
      aria-label="Breadcrumb"
      className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground"
    >
      {crumbs.map((crumb, idx) => {
        const isLast = idx === crumbs.length - 1;
        return (
          <Fragment key={idx}>
            {crumb.to && !isLast ? (
              <Link
                to={crumb.to}
                className="max-w-[14rem] truncate hover:text-primary hover:underline"
              >
                {crumb.label}
              </Link>
            ) : (
              <span
                className={
                  isLast ? 'max-w-[18rem] truncate text-foreground' : 'max-w-[14rem] truncate'
                }
              >
                {crumb.label}
              </span>
            )}
            {!isLast && <ChevronRight className="h-3 w-3 shrink-0 opacity-50" />}
          </Fragment>
        );
      })}
    </nav>
  );
}
